"use client";

import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
  Loader,
} from "@devhop/ui";
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  ImageOffIcon,
  XIcon,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { resolveImageSrc } from "@/shared/ui/AppImage";
import type { ShopSellableCatalogRow } from "../api/client";
import { useShopSkuMediaQuery } from "../api/queries";

type ShopSkuGalleryModalProps = {
  shopId: string;
  row: ShopSellableCatalogRow;
  onClose: () => void;
};

/** ชื่อไฟล์ท้าย key — ใช้แสดงแทนรูปที่โหลดไม่ขึ้น */
function fileNameOf(url: string): string {
  const parts = url.split("/");
  return parts[parts.length - 1] || url;
}

export function ShopSkuGalleryModal({
  shopId,
  row,
  onClose,
}: ShopSkuGalleryModalProps) {
  const mediaQuery = useShopSkuMediaQuery(shopId, row.id);
  const items = useMemo(() => mediaQuery.data ?? [], [mediaQuery.data]);
  const total = items.length;

  const [index, setIndex] = useState(0);
  const [brokenUrls, setBrokenUrls] = useState<Set<string>>(new Set());
  const [stageLoaded, setStageLoaded] = useState(false);

  // ถ้ารูปถูกลบระหว่างเปิดอยู่ ให้ index ไม่หลุดขอบ
  useEffect(() => {
    setIndex((i) => (total === 0 ? 0 : Math.min(i, total - 1)));
  }, [total]);

  const step = useCallback(
    (delta: number) => {
      if (total < 2) return;
      setStageLoaded(false);
      setIndex((i) => (i + delta + total) % total);
    },
    [total],
  );

  const jumpTo = useCallback(
    (next: number) => {
      if (next === index) return;
      setStageLoaded(false);
      setIndex(next);
    },
    [index],
  );

  // ArrowLeft/ArrowRight — Radix จัดการ Escape/backdrop/focus ให้แล้ว
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "ArrowLeft") {
        e.preventDefault();
        step(-1);
      } else if (e.key === "ArrowRight") {
        e.preventDefault();
        step(1);
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [step]);

  // preload เพื่อนบ้าน index ± 1
  useEffect(() => {
    if (total < 2) return;
    for (const delta of [-1, 1]) {
      const neighbour = items[(index + delta + total) % total];
      const src = resolveImageSrc(neighbour?.url);
      if (src) new Image().src = src;
    }
  }, [index, items, total]);

  const current = items[index];
  const currentSrc = resolveImageSrc(current?.url);
  const currentBroken = current ? brokenUrls.has(current.url) : false;

  const markBroken = useCallback((url: string) => {
    setBrokenUrls((prev) => {
      if (prev.has(url)) return prev;
      const next = new Set(prev);
      next.add(url);
      return next;
    });
  }, []);

  return (
    <Dialog
      open
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
    >
      <DialogContent
        showCloseButton={false}
        className="flex max-h-[min(94vh,760px)] w-[calc(100%-1rem)] max-w-none flex-col gap-0 overflow-hidden p-0 sm:max-w-4xl"
      >
        <div className="flex shrink-0 items-start justify-between gap-3 border-border border-b bg-muted/25 px-4 py-3 sm:px-5">
          <div className="min-w-0">
            <DialogTitle className="truncate text-base sm:text-lg">
              {row.productName}
            </DialogTitle>
            <DialogDescription className="truncate font-mono text-xs">
              SKU {row.catalogSkuId}
            </DialogDescription>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            {total > 0 && (
              <span className="text-muted-foreground text-sm tabular-nums">
                {index + 1} / {total}
              </span>
            )}
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="size-8"
              onClick={onClose}
              aria-label="ປິດ"
            >
              <XIcon className="size-4" aria-hidden />
            </Button>
          </div>
        </div>

        <div className="relative flex h-[460px] shrink-0 items-center justify-center bg-black/90">
          {mediaQuery.isLoading ? (
            <Loader />
          ) : mediaQuery.isError ? (
            <div className="px-6 text-center">
              <p className="text-sm text-white/80">ໂຫຼດຮູບລົ້ມເຫຼວ</p>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="mt-3"
                onClick={() => mediaQuery.refetch()}
              >
                ລອງໃໝ່
              </Button>
            </div>
          ) : total === 0 ? (
            <p className="px-6 text-center text-sm text-white/70">
              ຍັງບໍ່ມີຮູບສຳລັບ SKU ນີ້
            </p>
          ) : currentBroken || !currentSrc ? (
            <div className="flex flex-col items-center gap-2 px-6 text-center">
              <ImageOffIcon className="size-8 text-white/50" aria-hidden />
              <p className="break-all font-mono text-white/70 text-xs">
                {current ? fileNameOf(current.url) : "—"}
              </p>
            </div>
          ) : (
            <>
              {!stageLoaded && (
                <div className="absolute inset-0 flex items-center justify-center">
                  <Loader />
                </div>
              )}
              <img
                key={current?.id}
                src={currentSrc}
                alt={`${row.productName} — ຮູບທີ ${index + 1}`}
                decoding="async"
                className="size-full object-contain"
                onLoad={() => setStageLoaded(true)}
                onError={() => {
                  setStageLoaded(true);
                  if (current) markBroken(current.url);
                }}
              />
            </>
          )}

          {total > 1 && (
            <>
              <Button
                type="button"
                variant="secondary"
                size="icon"
                className="absolute top-1/2 left-3 size-9 -translate-y-1/2 rounded-full"
                onClick={() => step(-1)}
                aria-label="ກ່ອນໜ້າ"
              >
                <ChevronLeftIcon className="size-5" aria-hidden />
              </Button>
              <Button
                type="button"
                variant="secondary"
                size="icon"
                className="absolute top-1/2 right-3 size-9 -translate-y-1/2 rounded-full"
                onClick={() => step(1)}
                aria-label="ຖັດໄປ"
              >
                <ChevronRightIcon className="size-5" aria-hidden />
              </Button>
            </>
          )}
        </div>

        {total > 0 && (
          <div className="flex shrink-0 gap-2 overflow-x-auto border-border border-t bg-card px-4 py-3 sm:px-5">
            {items.map((m, i) => {
              const thumbSrc = resolveImageSrc(m.url);
              const broken = brokenUrls.has(m.url);
              return (
                <button
                  key={m.id}
                  type="button"
                  onClick={() => jumpTo(i)}
                  aria-label={`ໄປຮູບທີ ${i + 1}`}
                  aria-current={i === index}
                  className={`relative size-16 shrink-0 overflow-hidden rounded-md border bg-muted/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
                    i === index
                      ? "border-primary ring-2 ring-primary"
                      : "border-border hover:border-primary/60"
                  }`}
                >
                  {thumbSrc && !broken ? (
                    <img
                      src={thumbSrc}
                      alt=""
                      loading="lazy"
                      decoding="async"
                      className="size-full object-cover"
                      onError={() => markBroken(m.url)}
                    />
                  ) : (
                    <ImageOffIcon
                      className="size-5 text-muted-foreground"
                      aria-hidden
                    />
                  )}
                  <span className="absolute bottom-0 left-0 rounded-tr bg-foreground/70 px-1 text-[10px] text-background tabular-nums">
                    {i + 1}
                  </span>
                </button>
              );
            })}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
